import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * StrictMode-safe session_id exchange.
 *
 * React 18 in dev intentionally mounts → unmounts → remounts components to
 * surface state-bug regressions. `useRef` is reset between mounts, so a naive
 * "processed" flag fires the API call twice. The second call uses an already-
 * consumed OAuth session_id, /api/auth/session returns 401, the .catch() sends
 * the user back to "/" — that is the exact "login keeps bringing me to landing"
 * loop.
 *
 * Module-level Map (persists across mounts) tracks each sid's state:
 *   "pending" — we've started the exchange; another mount should sit tight.
 *   "done"    — we already auth'd this sid successfully (e.g. user hit back).
 */
const sidStatus = new Map();

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const sid = decodeURIComponent(m[1]);
    const status = sidStatus.get(sid);
    if (status === "pending") {
      // Another mount of this same effect is already in flight (StrictMode).
      // Do nothing — when its promise resolves it navigates for us.
      return;
    }
    if (status === "done") {
      // Back-button to the original OAuth URL after we'd already auth'd.
      // The session_token cookie is still valid; go straight to dashboard.
      navigate("/dashboard", { replace: true });
      return;
    }
    sidStatus.set(sid, "pending");
    const ref = localStorage.getItem("vit_ref") || "";
    api.session(sid, ref)
      .then(({ user }) => {
        sidStatus.set(sid, "done");
        if (ref) {
          try { api.attributeAffiliate(ref); } catch (_) {}
        }
        setUser(user);
        navigate("/dashboard", { replace: true, state: { user } });
      })
      .catch(() => {
        sidStatus.delete(sid);  // allow retry from a fresh login click
        navigate("/", { replace: true });
      });
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-eyebrow">Authenticating…</div>
    </div>
  );
}
